/**
 * Admin view of a single contractor verification file — everything the
 * contractor submitted, in one place: company details, the owner behind the
 * company (linked to their user page), the requirements checklist, uploaded
 * documents, and the portfolio with photos (URLs are pre-signed server-side
 * by GET /contractor-verifications/admin/:id, so images render directly).
 *
 * It also carries the verifier's field report — the assignment's findings, the
 * representative they met on site, and each site visit — from
 * GET /contractor-verifications/admin/:id/evidence-pack. That side is what the
 * verifier produces; without it an admin decides on the contractor's own
 * submission alone.
 *
 * Reached by clicking a company name on /admin/home/verifications?kind=contractor.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Chip, Link, Grid, Alert, CircularProgress, Button,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { ArrowBack, OpenInNew, VerifiedUser } from '@mui/icons-material';
import adminService from '../services/adminService';
import AssignVerifierButton from '../components/admin/AssignVerifierButton';
import ApplicationActions from '../components/admin/ApplicationActions';
import adminApplicationsService from '../services/adminApplicationsService';
import { panelSx, heroPanelSx, panelHeaderSx, tableSx } from '../components/admin/surfaces';
import { brand, status as statusTokens, text, bg, border, verifier as verifierTone } from '../theme/colors';
import { formatCurrency } from '../utils/helpers';

const EMPTY = '—';
const titleise = (value) => String(value || '').replace(/_/g, ' ');
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : EMPTY);

const STATUS_TONES = {
  pending: statusTokens.warning,
  in_review: statusTokens.info,
  submitted: statusTokens.warning,
  changes_requested: statusTokens.warning,
  verified: statusTokens.success,
  approved: statusTokens.success,
  completed: statusTokens.success,
  rejected: statusTokens.error,
  cancelled: statusTokens.neutral,
  expired: statusTokens.neutral,
};

// contractor_verification_assignments_risk_level_check
const RISK_TONES = {
  low: statusTokens.successDark,
  standard: statusTokens.infoDark,
  elevated: statusTokens.warningDark,
  high: statusTokens.errorDark,
};

const toneChip = (value) => {
  if (!value) return EMPTY;
  const tone = STATUS_TONES[value] || statusTokens.neutral;
  return (
    <Chip
      size="small"
      label={titleise(value)}
      sx={{ color: tone, bgcolor: `${tone}1a`, textTransform: 'capitalize', fontWeight: 600 }}
    />
  );
};

/** evidence_urls is stored as JSON (array) but may arrive as a string. */
const asUrlList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [value];
    } catch (_e) {
      return [value];
    }
  }
  return [];
};

const Row = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, py: 0.75, borderBottom: `1px dashed ${border.light}`, '&:last-child': { borderBottom: 'none' } }}>
    <Typography variant="body2" sx={{ color: text.tertiary, minWidth: 170, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography component="div" variant="body2" sx={{ color: text.primary, wordBreak: 'break-word' }}>
      {children ?? EMPTY}
    </Typography>
  </Box>
);

const ReviewButtons = ({ item, status, busy, onReview }) => (
  <Box sx={{ display: 'inline-flex', gap: 0.75 }}>
    {status !== 'verified' && (
      <Button size="small" variant="outlined" color="success" disabled={busy}
        sx={{ textTransform: 'none', fontWeight: 600, py: 0, minWidth: 0 }}
        onClick={() => onReview(item, 'approved')}>
        Approve
      </Button>
    )}
    {status !== 'rejected' && (
      <Button size="small" color="error" disabled={busy}
        sx={{ textTransform: 'none', fontWeight: 600, py: 0, minWidth: 0 }}
        onClick={() => onReview(item, 'rejected')}>
        Reject
      </Button>
    )}
  </Box>
);

/**
 * `accent` marks a panel as carrying independent, third-party findings rather
 * than the contractor's own submission — same geometry as every other panel so
 * it still reads as family, but a verifier-toned rail, header and icon so an
 * admin can tell at a glance whose word they are reading.
 */
const Panel = ({ title, subtitle, icon, action, accent = false, children }) => (
  <Box
    sx={{
      ...panelSx,
      ...(accent ? { borderLeft: `3px solid ${verifierTone.dark}` } : {}),
    }}
  >
    <Box
      sx={{
        ...panelHeaderSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        ...(accent ? { bgcolor: verifierTone.light, borderBottomColor: `${verifierTone.dark}33` } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {icon && (
          <Box sx={{ display: 'grid', placeItems: 'center', color: verifierTone.dark, '& svg': { fontSize: 19 } }}>
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: accent ? verifierTone.dark : text.primary, fontSize: 15 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: text.secondary, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action || null}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

export default function AdminContractorVerificationDetailPage() {
  const { verificationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [file, setFile] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  // id of the evidence item an approve/reject call is in flight for
  const [acting, setActing] = useState(null);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async ({ quiet } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      // The field report is supplementary: a file with no verifier assigned has
      // none, and a failure there must not blank the contractor's submission.
      const [data, pack] = await Promise.all([
        adminService.getContractorVerificationFile(verificationId),
        adminService.getContractorVerificationEvidencePack(verificationId).catch(() => null),
      ]);
      setFile(data);
      setEvidence(pack);
      // The contractor's application record drives the approve/reject controls.
      // Supplementary: a failure here must not blank the verification view.
      if (data?.contractor_id) {
        const appRes = await adminApplicationsService.detail(data.contractor_id).catch(() => null);
        setApplication(appRes?.data || null);
      } else {
        setApplication(null);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load the verification file');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [verificationId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Review a document row. Rows synced from the Applications flow carry
   * user_document_id — those go through the canonical endpoint (the same
   * write the Applications dialog does, so the two screens agree); rows the
   * verification wizard created natively go through the snapshot endpoint.
   */
  const reviewDocument = async (doc, decision) => {
    setActing(doc.id);
    setActionError('');
    try {
      if (doc.user_document_id) {
        await adminService.reviewUserDocument(doc.user_document_id, decision);
      } else {
        await adminService.reviewVerificationEvidence(
          'documents', doc.id, decision === 'approved' ? 'verified' : 'rejected'
        );
      }
      await load({ quiet: true });
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.response?.data?.error || 'Review action failed');
    } finally {
      setActing(null);
    }
  };

  const reviewPortfolio = async (item, decision) => {
    setActing(item.id);
    setActionError('');
    try {
      if (item.id === 'onboarding-photos') {
        // The synthetic card is a bundle of user_documents photo rows.
        for (const docId of item.photo_document_ids || []) {
          // Sequential on purpose: tiny counts, and it keeps audit order sane.
          // eslint-disable-next-line no-await-in-loop
          await adminService.reviewUserDocument(docId, decision);
        }
      } else {
        await adminService.reviewVerificationEvidence(
          'portfolio', item.id, decision === 'approved' ? 'verified' : 'rejected'
        );
      }
      await load({ quiet: true });
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.response?.data?.error || 'Review action failed');
    } finally {
      setActing(null);
    }
  };

  const backToList = () => {
    const { listPath, listSearch } = location.state || {};
    if (listPath) navigate(`${listPath}${listSearch || ''}`);
    else navigate('/admin/home/verifications?kind=contractor');
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error || !file) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={backToList} sx={{ mb: 2, color: text.secondary }}>
          Back to verifications
        </Button>
        <Alert severity="error">{error || 'Verification file not found'}</Alert>
      </Box>
    );
  }

  const ownerName = `${file.first_name || ''} ${file.last_name || ''}`.trim();
  // Same fallback chain as the list page: company → trading → legal → person.
  const companyName = (file.company_name || '').trim()
    || file.trading_name || file.legal_name || ownerName || 'Unnamed company';
  // The verification file's own columns are often empty (files created by
  // backfill, details captured during onboarding instead) — fall back to the
  // profile_* values hydrate() now exposes.
  const asList = (v) => (Array.isArray(v) ? v : asUrlList(v));
  const specializations = asList(file.specializations).length
    ? asList(file.specializations)
    : asList(file.profile_specializations);
  const serviceAreas = asList(file.service_areas).length
    ? asList(file.service_areas)
    : asList(file.profile_service_areas);
  const registrationNumber = file.company_registration_number || file.profile_registration_number;
  const primaryTrade = file.primary_trade || file.profile_primary_trade;
  const operatingAddress = file.operating_address
    || [file.address, file.suburb, file.city, file.province].filter(Boolean).join(', ');
  const profileLocation = [file.city, file.province].filter(Boolean).join(', ');
  const portfolio = file.portfolioEvidence || [];
  const documents = file.documents || [];
  const references = file.references || [];
  const requirements = file.requirements || [];

  // The verifier's side of the file (null until someone is assigned).
  const assignment = evidence?.assignment || null;
  const representativeCheck = evidence?.representativeCheck || null;
  const siteVisits = evidence?.siteVisits || [];

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Button startIcon={<ArrowBack />} onClick={backToList} sx={{ mb: 2, color: text.secondary }}>
        Back to verifications
      </Button>

      {/* Hero: who this file is about, at a glance */}
      <Box sx={{ ...heroPanelSx, p: 3, mb: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          {file.business_photo_url || file.profile_image_url ? (
            <Box
              component="img"
              src={file.business_photo_url || file.profile_image_url}
              alt=""
              sx={{ width: 56, height: 56, borderRadius: 2, objectFit: 'cover', border: `1px solid ${border.light}` }}
            />
          ) : null}
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>
              {companyName}
            </Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>
              {ownerName ? (
                <>
                  Owner:{' '}
                  {file.contractor_id ? (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => navigate(`/admin/home/users/${file.contractor_id}`)}
                      sx={{ color: brand.primary, fontWeight: 600, fontSize: 'inherit', fontFamily: 'inherit', verticalAlign: 'baseline' }}
                    >
                      {ownerName}
                    </Link>
                  ) : ownerName}
                </>
              ) : 'No platform account on file'}
              {profileLocation ? ` · ${profileLocation}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {toneChip(file.verification_status || file.status)}
            {file.badge_type ? (
              <Chip size="small" label={titleise(file.badge_type)} sx={{ bgcolor: bg.subtle, textTransform: 'capitalize' }} />
            ) : null}
            {file.score != null ? (
              <Chip size="small" label={`Score ${file.score}`} sx={{ bgcolor: bg.subtle }} />
            ) : null}
            {file.risk_level ? (
              <Chip size="small" label={`Risk: ${titleise(file.risk_level)}`} sx={{ bgcolor: bg.subtle, textTransform: 'capitalize' }} />
            ) : null}
          </Box>
        </Box>

        {/* Assignment — its own bordered strip, laid out as a labelled row so
            the verifier, state and controls each have room to read. */}
        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${border.light}`,
            bgcolor: bg.subtle,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <VerifiedUser sx={{ fontSize: 22, color: verifierTone.dark }} />

          {file.assignment?.verifier && file.assignment.status !== 'cancelled' ? (
            <>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', display: 'block' }}>
                  Assigned verifier
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                  {file.assignment.verifier.id ? (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => navigate(`/admin/home/users/${file.assignment.verifier.id}`)}
                      sx={{ color: text.primary, fontWeight: 700, fontSize: 16, fontFamily: 'inherit', verticalAlign: 'baseline', '&:hover': { color: brand.primary } }}
                    >
                      {file.assignment.verifier.name}
                    </Link>
                  ) : (
                    <Typography sx={{ color: text.primary, fontWeight: 700, fontSize: 16 }}>
                      {file.assignment.verifier.name}
                    </Typography>
                  )}
                  {file.assignment.status ? toneChip(file.assignment.status) : null}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  {file.assignment.dueAt ? (
                    <Typography variant="caption" sx={{ color: text.secondary }}>
                      Due {formatDate(file.assignment.dueAt)}
                    </Typography>
                  ) : null}
                  {file.assignment.verifier.id ? (
                    <>
                      {file.assignment.dueAt ? <Typography variant="caption" sx={{ color: text.tertiary }}>·</Typography> : null}
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => navigate(`/admin/home/users/${file.assignment.verifier.id}/verifications`)}
                        sx={{ color: brand.primary, fontWeight: 600, fontSize: 12, fontFamily: 'inherit', verticalAlign: 'baseline' }}
                      >
                        Their other work
                      </Link>
                    </>
                  ) : null}
                </Box>
              </Box>

              {/* Reassign: same endpoint, which cancels the old assignment and
                  opens a fresh one for the new verifier. */}
              <AssignVerifierButton
                kind="contractor"
                row={{ verificationId, contractor: { name: companyName } }}
                label="Change"
                variant="outlined"
                currentVerifierId={file.assignment.verifier.id}
                currentVerifierName={file.assignment.verifier.name}
                onAssigned={() => load({ quiet: true })}
              />
              {['assigned', 'in_progress', 'changes_requested'].includes(file.assignment.status) && (
                <Button variant="text" color="error" size="small"
                  onClick={() => { setRemoveReason(''); setRemoveError(''); setRemoveOpen(true); }}>
                  Remove
                </Button>
              )}
            </>
          ) : (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', display: 'block' }}>
                  Assigned verifier
                </Typography>
                <Typography variant="body2" sx={{ color: text.secondary, mt: 0.25 }}>
                  {file.assignment?.status === 'cancelled' && file.assignment.verifier?.name
                    ? `No verifier assigned. ${file.assignment.verifier.name} was removed.`
                    : 'No verifier assigned yet'}
                </Typography>
              </Box>
              <AssignVerifierButton
                kind="contractor"
                row={{ verificationId, contractor: { name: companyName } }}
                onAssigned={() => load({ quiet: true })}
              />
            </>
          )}
        </Box>
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      {/* Application decision — the same approve/reject actions as
          /admin/applications, surfaced here so an admin can decide the
          contractor's application right after reviewing the evidence, instead
          of going back to the applications list. Backed by the contractor's
          application record (approval_status etc.). */}
      {application && (
        <Box sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, color: text.primary, fontSize: 15, mb: 0.5 }}>
            Application decision
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 1.75 }}>
            Approving grants the contractor full access and emails them. Approval is
            blocked until every mandatory requirement is satisfied or waived.
          </Typography>
          <ApplicationActions application={application} onChanged={() => load({ quiet: true })} />
        </Box>
      )}

      <Grid container spacing={2.5}>
        {/* Business details as submitted */}
        <Grid item xs={12} md={6}>
          <Panel title="Business details">
            <Row label="Legal name">{file.legal_name}</Row>
            <Row label="Trading name">{file.trading_name}</Row>
            <Row label="Registration number">{registrationNumber}</Row>
            <Row label="Tax identifier">{file.tax_identifier}</Row>
            <Row label="Tax clearance">
              {file.tax_clearance_number
                ? `${file.tax_clearance_number}${file.tax_clearance_expires_at ? ` (expires ${formatDate(file.tax_clearance_expires_at)})` : ''}`
                : EMPTY}
            </Row>
            <Row label="Primary trade">{titleise(primaryTrade) || EMPTY}</Row>
            <Row label="Specializations">
              {specializations.length ? specializations.map(titleise).join(', ') : EMPTY}
            </Row>
            <Row label="Service areas">
              {serviceAreas.length ? serviceAreas.join(', ') : EMPTY}
            </Row>
            <Row label="Operating address">{operatingAddress || EMPTY}</Row>
            <Row label="Years experience">{file.profile_years_experience}</Row>
            <Row label="Submitted">{formatDate(file.submitted_at)}</Row>
          </Panel>
        </Grid>

        {/* Owner & contact */}
        <Grid item xs={12} md={6}>
          <Panel title="Owner & contact">
            <Row label="Owner">
              {file.contractor_id && ownerName ? (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => navigate(`/admin/home/users/${file.contractor_id}`)}
                  sx={{ color: brand.primary, fontWeight: 600, fontSize: 'inherit', fontFamily: 'inherit' }}
                >
                  {ownerName}
                </Link>
              ) : (ownerName || EMPTY)}
            </Row>
            <Row label="Email">{file.contractor_email}</Row>
            <Row label="Phone">{file.phone_number}</Row>
            <Row label="Address">{[file.address, file.suburb, file.city, file.province].filter(Boolean).join(', ') || EMPTY}</Row>
          </Panel>

          <Box sx={{ mt: 2.5 }}>
            <Panel title="Requirements checklist">
              {requirements.length === 0 ? (
                <Typography variant="body2" sx={{ color: text.tertiary }}>No requirements recorded.</Typography>
              ) : requirements.map((r) => (
                <Box key={r.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, gap: 1, borderBottom: `1px dashed ${border.light}`, '&:last-child': { borderBottom: 'none' } }}>
                  <Typography variant="body2" sx={{ color: text.primary }}>
                    {r.label}
                    {r.required === false ? (
                      <Typography component="span" variant="caption" sx={{ color: text.tertiary }}> (optional)</Typography>
                    ) : null}
                  </Typography>
                  {toneChip(r.status || (r.verified ? 'verified' : 'pending'))}
                </Box>
              ))}
            </Panel>
          </Box>
        </Grid>

        {/* Portfolio with photos */}
        <Grid item xs={12}>
          <Panel title={`Portfolio (${portfolio.length})`}>
            {portfolio.length === 0 ? (
              <Typography variant="body2" sx={{ color: text.tertiary }}>
                No portfolio projects submitted.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {portfolio.map((item) => {
                  const photos = asUrlList(item.evidence_urls);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Box sx={{ border: `1px solid ${border.light}`, borderRadius: 2, overflow: 'hidden', height: '100%' }}>
                        {photos[0] ? (
                          <Box
                            component="img"
                            src={photos[0]}
                            alt={item.project_title || 'Portfolio photo'}
                            onClick={() => setLightbox(photos[0])}
                            sx={{ width: '100%', height: 150, objectFit: 'cover', display: 'block', cursor: 'zoom-in', bgcolor: bg.subtle }}
                          />
                        ) : (
                          <Box sx={{ height: 150, bgcolor: bg.subtle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" sx={{ color: text.tertiary }}>No photos</Typography>
                          </Box>
                        )}
                        <Box sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }} noWrap title={item.project_title || ''}>
                              {item.project_title || 'Untitled project'}
                            </Typography>
                            {toneChip(item.status)}
                          </Box>
                          <Box sx={{ mt: 0.75 }}>
                            <ReviewButtons
                              item={item}
                              status={item.status}
                              busy={acting === item.id}
                              onReview={reviewPortfolio}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: text.secondary, display: 'block' }}>
                            {[item.project_location, item.completed_at ? formatDate(item.completed_at) : null]
                              .filter(Boolean).join(' · ') || EMPTY}
                          </Typography>
                          <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                            {item.project_value != null && item.project_value !== ''
                              ? formatCurrency(item.project_value, item.currency || 'USD')
                              : null}
                            {item.client_name ? `${item.project_value ? ' · ' : ''}Client: ${item.client_name}` : null}
                          </Typography>
                          {photos.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                              {photos.slice(1, 6).map((url) => (
                                <Box
                                  key={url}
                                  component="img"
                                  src={url}
                                  alt=""
                                  onClick={() => setLightbox(url)}
                                  sx={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', border: `1px solid ${border.light}` }}
                                />
                              ))}
                              {photos.length > 6 && (
                                <Typography variant="caption" sx={{ color: text.tertiary, alignSelf: 'center' }}>
                                  +{photos.length - 6}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Panel>
        </Grid>

        {/* Documents */}
        <Grid item xs={12} md={references.length ? 7 : 12}>
          <Panel title={`Documents (${documents.length})`}>
            {documents.length === 0 ? (
              <Typography variant="body2" sx={{ color: text.tertiary }}>No documents uploaded.</Typography>
            ) : (
              <Table size="small" sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">File</TableCell>
                    <TableCell align="right">Review</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell sx={{ textTransform: 'capitalize', color: text.primary }}>
                        {titleise(doc.document_type)}
                        {doc.document_name ? (
                          <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }} noWrap title={doc.document_name}>
                            {doc.document_name}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{toneChip(doc.status)}</TableCell>
                      <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                        {doc.expires_at ? formatDate(doc.expires_at) : EMPTY}
                      </TableCell>
                      <TableCell align="right">
                        {doc.file_url ? (
                          <Link href={doc.file_url} target="_blank" rel="noopener" sx={{ color: brand.primary, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            Open <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        ) : EMPTY}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <ReviewButtons
                          item={doc}
                          status={doc.status}
                          busy={acting === doc.id}
                          onReview={reviewDocument}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Panel>
        </Grid>

        {/* References, only when any exist (they're optional since 2026-06) */}
        {references.length > 0 && (
          <Grid item xs={12} md={5}>
            <Panel title={`References (${references.length})`}>
              {references.map((ref) => (
                <Box key={ref.id} sx={{ py: 0.75, borderBottom: `1px dashed ${border.light}`, '&:last-child': { borderBottom: 'none' } }}>
                  <Typography variant="body2" sx={{ color: text.primary, fontWeight: 600 }}>
                    {ref.client_name || ref.contact_name || 'Reference'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: text.secondary }}>
                    {[ref.contact_phone || ref.client_phone, ref.project_title, ref.relationship]
                      .filter(Boolean).join(' · ') || EMPTY}
                  </Typography>
                </Box>
              ))}
            </Panel>
          </Grid>
        )}
      </Grid>

      {/* What the verifier found on the ground — the other half of the file. */}
      <Box sx={{ mt: 2.5 }}>
        <Panel
          accent
          icon={<VerifiedUser />}
          title="Verifier field report"
          subtitle="Independently checked on the ground — not the contractor's own submission"
          action={assignment?.verifier?.name || file?.assignment?.verifier?.name ? (
            <Typography variant="caption" sx={{ color: text.secondary }}>
              {assignment?.verifier?.name || file?.assignment?.verifier?.name}
            </Typography>
          ) : null}
        >
          {!assignment ? (
            <Typography variant="body2" sx={{ color: text.tertiary }}>
              No verifier has been assigned yet, so there is no field report. Assign one to start it.
            </Typography>
          ) : (
            <>
              {/* Verdict */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                    Recommendation
                  </Typography>
                  {assignment.recommendation
                    ? toneChip(assignment.recommendation === 'approve' ? 'approved' : assignment.recommendation)
                    : <Typography variant="body2" sx={{ color: text.tertiary }}>Not given yet</Typography>}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                    Risk level
                  </Typography>
                  <Typography sx={{ color: RISK_TONES[assignment.risk_level] || text.tertiary, fontWeight: 700, textTransform: 'capitalize' }}>
                    {titleise(assignment.risk_level) || EMPTY}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                    Confidence
                  </Typography>
                  <Typography sx={{ color: text.primary, fontWeight: 700 }}>
                    {assignment.confidence_score != null ? `${assignment.confidence_score}%` : EMPTY}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                    Submitted
                  </Typography>
                  <Typography sx={{ color: text.primary, fontWeight: 600 }}>
                    {formatDate(assignment.submitted_at)}
                  </Typography>
                </Grid>
              </Grid>

              {assignment.verifier_summary && (
                <Box sx={{ mb: 2, p: 1.75, borderRadius: 1.5, bgcolor: bg.subtle, border: `1px solid ${border.light}` }}>
                  <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mb: 0.5 }}>
                    Verifier's summary
                  </Typography>
                  <Typography variant="body2" sx={{ color: text.primary, whiteSpace: 'pre-wrap' }}>
                    {assignment.verifier_summary}
                  </Typography>
                </Box>
              )}

              {/* GPS proof was overridden — decision-relevant, so never hide it. */}
              {assignment.gps_proof_mode === 'admin_override' && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
                  GPS proof was waived for this assignment
                  {assignment.gps_override_reason ? `: ${assignment.gps_override_reason}` : '.'}
                </Alert>
              )}

              {/* Who they met on site */}
              <Typography variant="caption" sx={{ color: text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 1 }}>
                Representative met on site
              </Typography>
              {!representativeCheck ? (
                <Typography variant="body2" sx={{ color: text.tertiary, mb: 2 }}>
                  Not recorded.
                </Typography>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                    <Typography variant="body2" sx={{ color: text.primary, fontWeight: 600 }}>
                      {representativeCheck.representative_name || EMPTY}
                    </Typography>
                    {representativeCheck.representative_role && (
                      <Typography variant="caption" sx={{ color: text.secondary }}>
                        {representativeCheck.representative_role}
                      </Typography>
                    )}
                    {toneChip(representativeCheck.status)}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[
                      ['ID document', representativeCheck.id_document_checked],
                      ['Company authority', representativeCheck.company_authority_checked],
                      ['Phone confirmed', representativeCheck.phone_confirmed],
                      ['Email confirmed', representativeCheck.email_confirmed],
                    ].map(([label, ok]) => (
                      <Typography
                        key={label}
                        variant="caption"
                        sx={{ color: ok ? statusTokens.successDark : text.tertiary, fontWeight: 600 }}
                      >
                        {ok ? '✓' : '✗'} {label}
                      </Typography>
                    ))}
                  </Box>
                  {representativeCheck.notes && (
                    <Typography variant="body2" sx={{ color: text.secondary, mt: 0.75, whiteSpace: 'pre-wrap' }}>
                      {representativeCheck.notes}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Site visits */}
              <Typography variant="caption" sx={{ color: text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 1 }}>
                Site visits ({siteVisits.length}
                {assignment.required_site_visit_count ? ` of ${assignment.required_site_visit_count} required` : ''})
              </Typography>

              {/* An unmet visit quota is the thing an admin needs to notice. */}
              {assignment.required_site_visit_count > siteVisits.length && (
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 1.5 }}>
                  {siteVisits.length} of {assignment.required_site_visit_count} required site visits recorded.
                </Alert>
              )}

              {siteVisits.length === 0 ? (
                <Typography variant="body2" sx={{ color: text.tertiary }}>
                  No site visits recorded.
                </Typography>
              ) : siteVisits.map((visit) => {
                const photos = asUrlList(visit.photo_urls);
                return (
                  <Box
                    key={visit.id}
                    sx={{ py: 1.25, borderBottom: `1px dashed ${border.light}`, '&:last-child': { borderBottom: 'none' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ color: text.primary, fontWeight: 600 }}>
                        {visit.project_title || 'Site visit'}
                      </Typography>
                      {toneChip(visit.visit_status)}
                      {visit.gps_status && (
                        <Chip
                          size="small"
                          label={`GPS ${titleise(visit.gps_status)}${
                            visit.distance_km != null ? ` · ${Number(visit.distance_km).toFixed(1)} km` : ''
                          }`}
                          sx={{
                            fontSize: 11,
                            height: 22,
                            color: visit.gps_status === 'verified' ? statusTokens.successDark
                              : visit.gps_status === 'failed' ? statusTokens.errorDark : statusTokens.warningDark,
                            bgcolor: visit.gps_status === 'verified' ? statusTokens.successBg
                              : visit.gps_status === 'failed' ? statusTokens.errorBg : statusTokens.warningBg,
                          }}
                        />
                      )}
                    </Box>

                    <Typography variant="caption" sx={{ color: text.secondary, display: 'block' }}>
                      {[
                        visit.project_location,
                        visit.site_contact_name && `met ${visit.site_contact_name}`,
                        visit.site_contact_phone,
                        visit.visited_at && formatDate(visit.visited_at),
                      ].filter(Boolean).join(' · ') || EMPTY}
                    </Typography>

                    {visit.notes && (
                      <Typography variant="body2" sx={{ color: text.secondary, mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {visit.notes}
                      </Typography>
                    )}

                    {photos.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {photos.map((url) => (
                          <Box
                            key={url}
                            component="img"
                            src={url}
                            alt=""
                            onClick={() => setLightbox(url)}
                            sx={{
                              width: 84, height: 84, objectFit: 'cover', borderRadius: 1,
                              border: `1px solid ${border.light}`, cursor: 'zoom-in',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}

              {assignment.admin_feedback && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5 }}>
                  <strong>Admin feedback to the verifier:</strong> {assignment.admin_feedback}
                </Alert>
              )}
            </>
          )}
        </Panel>
      </Box>

      {/* Click-to-zoom for portfolio photos */}
      {lightbox && (
        <Box
          onClick={() => setLightbox(null)}
          sx={{
            position: 'fixed', inset: 0, zIndex: 1400, bgcolor: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', p: 3,
          }}
        >
          <Box component="img" src={lightbox} alt="" sx={{ maxWidth: '95%', maxHeight: '95%', borderRadius: 1 }} />
        </Box>
      )}

      <Dialog open={removeOpen} onClose={removing ? undefined : () => setRemoveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {file.assignment?.verifier?.name || 'the verifier'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
            The file comes off their list and they are told it was withdrawn. Nobody else is assigned.
            Anything they already recorded stays on the file.
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={2} label="Reason" value={removeReason}
            placeholder="e.g. Assigned by mistake: this contractor is already approved"
            onChange={(e) => setRemoveReason(e.target.value)} />
          {removeError && <Alert severity="error" sx={{ mt: 2 }}>{removeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOpen(false)} disabled={removing}>Cancel</Button>
          <Button color="error" variant="contained" disabled={removing || removeReason.trim().length < 3}
            onClick={async () => {
              setRemoving(true);
              setRemoveError('');
              try {
                await adminService.unassignContractorVerifier(verificationId, removeReason.trim());
                setRemoveOpen(false);
                await load({ quiet: true });
              } catch (e) {
                setRemoveError(e.response?.data?.message || 'Could not remove the verifier. Try again.');
              } finally {
                setRemoving(false);
              }
            }}>
            {removing ? 'Removing…' : 'Remove verifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
